/**
 * These colors are based on the Design System colors defined here: https://www.figma.com/design/ERy4kOqHNUdtBhGoyzf79F/Foundation-Library
 * Do not add any colors here which are not part of the Design System primitive or semantic colors (tokens).
 */
export enum COLORS {
  // PRIMITIVE COLORS
  Black = '#000000',
  White = '#FFFFFF',

  'Grey.Shade900' = '#262626',
  'Grey.Shade800' = '#434343',
  'Grey.Shade700' = '#555555',
  'Grey.Shade600' = '#7B7B7B',
  'Grey.Shade500' = '#9D9D9D',
  'Grey.Shade400' = '#C4C4C4',
  'Grey.Shade300' = '#D9D9D9',
  'Grey.Shade200' = '#E9E9E9',
  'Grey.Shade100' = '#F5F5F5',

  'Purple.Shade900' = '#1C023F',
  'Purple.Shade800' = '#300577',
  'Purple.Shade700' = '#4509B0',
  'Purple.Shade600' = '#6915E7',
  'Purple.Shade500' = '#803FED',
  'Purple.Shade400' = '#B186F6',
  'Purple.Shade300' = '#CDB3F7',
  'Purple.Shade200' = '#e9e9e9',
  'Purple.Shade100' = '#F5F2FF',

  'Blue.Shade900' = '#021440',
  'Blue.Shade800' = '#050F77',
  'Blue.Shade700' = '#1539CF',
  'Blue.Shade600' = '#1F4BFF',
  'Blue.Shade500' = '#3E70EE',
  'Blue.Shade400' = '#63A2F5',
  'Blue.Shade300' = '#8FC5FF',
  'Blue.Shade200' = '#C7DDFF',
  'Blue.Shade100' = '#E3ECF8',

  'Red.Shade900' = '#3E0203',
  'Red.Shade800' = '#770507',
  'Red.Shade700' = '#B10A11',
  'Red.Shade600' = '#D9232A',
  'Red.Shade500' = '#ED4043',
  'Red.Shade400' = '#F56464',
  'Red.Shade300' = '#FF8E8F',
  'Red.Shade200' = '#FFBDBF',
  'Red.Shade100' = '#FFE9EA',

  'Orange.Shade900' = '#402203',
  'Orange.Shade800' = '#773B06',
  'Orange.Shade700' = '#B0560A',
  'Orange.Shade600' = '#EF750D',
  'Orange.Shade500' = '#FFA600',
  'Orange.Shade400' = '#F4A963',
  'Orange.Shade300' = '#FFC58D',
  'Orange.Shade200' = '#FFDCBB',
  'Orange.Shade100' = '#FFF5E8',

  'Yellow.Shade900' = '#504100',
  'Yellow.Shade800' = '#7F6700',
  'Yellow.Shade700' = '#B59102',
  'Yellow.Shade600' = '#B59102',
  'Yellow.Shade500' = '#FFF200',
  'Yellow.Shade400' = '#F9FF00',
  'Yellow.Shade300' = '#FFF893',
  'Yellow.Shade200' = '#FFF9BC',
  'Yellow.Shade100' = '#FFFCE9',

  'Green.Shade900' = '#003222',
  'Green.Shade800' = '#024E36',
  'Green.Shade700' = '#087554',
  'Green.Shade600' = '#02976A',
  'Green.Shade500' = '#00D688',
  'Green.Shade400' = '#4FFFB3',
  'Green.Shade300' = '#9CFDD1',
  'Green.Shade200' = '#CBFFE4',
  'Green.Shade100' = '#EBFFF8',

  // SEMANTIC COLORS
  // Text
  'Text.Black' = COLORS.Black,
  'Text.White' = COLORS.White,
  'Text.Grey' = COLORS['Grey.Shade800'],
  'Text.Grey.Inactive' = COLORS['Grey.Shade500'],

  // Fill
  'Fill.Black' = COLORS.Black,
  'Fill.White' = COLORS.White,
  'Fill.Grey.Primary' = COLORS['Grey.Shade100'],
  'Fill.Grey.Secondary' = COLORS['Grey.Shade800'],
  'Fill.Grey.Inactive' = COLORS['Grey.Shade500'],

  // Buttons
  'Button.Primary.Hover' = COLORS['Grey.Shade900'],

  // Outline
  'Outline.Grey' = COLORS['Grey.Shade300'],

  // Low stock
  'LowStock' = COLORS['Yellow.Shade300'],

  // Coupons
  'Coupon.Primary' = COLORS['Blue.Shade700'],
  'Coupon.Secondary' = COLORS['Blue.Shade600'],
  'Coupon.Tertiary' = '#9BAAE6',
  'Coupon.Quaternary' = COLORS['Blue.Shade100'],

  // Coins
  'Coins.Green' = COLORS['Green.Shade200'],
  'Coins.Red' = COLORS['Red.Shade200'],
  'Coins.Orange' = COLORS['Orange.Shade200'],
  'Coins.Grey' = COLORS['Grey.Shade900'],

  // AI
  'Ai.Primary' = COLORS['Purple.Shade600'],
  'Ai.Secondary' = COLORS['Purple.Shade900'],
  'Ai.Tertiary' = COLORS['Purple.Shade100'],

  // Sale
  Sale = COLORS['Red.Shade600'],

  // Live shopping and Drops
  LiveShopping = COLORS['Yellow.Shade400'],
  Drops = COLORS['Yellow.Shade400'],

  // Alerts
  'Alerts.Error.Primary' = COLORS['Red.Shade600'],
  'Alerts.Error.Secondary' = COLORS['Red.Shade100'],
  'Alerts.Info.Primary' = COLORS['Grey.Shade600'],
  'Alerts.Info.Secondary' = COLORS['Grey.Shade100'],
  'Alerts.Success.Primary' = COLORS['Green.Shade700'],
  'Alerts.Success.Secondary' = COLORS['Green.Shade300'],
  'Alerts.Success.Tertiary' = COLORS['Green.Shade100'],
  'Alerts.Warning.Primary' = COLORS['Orange.Shade600'],
  'Alerts.Warning.Secondary' = COLORS['Orange.Shade100'],

  // DEPRECATED COLORS
  /**
   * @deprecated
   */
  Grey0 = COLORS['Grey.Shade800'],

  /**
   * @deprecated
   */
  Grey1 = COLORS['Grey.Shade800'],

  /**
   * @deprecated
   */
  Grey2 = COLORS['Grey.Shade500'],

  /**
   * @deprecated
   */
  Grey3 = COLORS['Grey.Shade300'],

  /**
   * @deprecated
   */
  Grey4 = COLORS['Grey.Shade100'],

  /**
   * @deprecated
   */
  Grey5 = COLORS['Grey.Shade100'],

  /**
   * @deprecated
   */
  Grey7 = COLORS['Outline.Grey'],

  /**
   * @deprecated
   */
  Orange = COLORS['Orange.Shade300'],

  /**
   * @deprecated
   */
  Yellow = COLORS['Yellow.Shade400'],

  /**
   * @deprecated
   */
  YellowGold = COLORS['Text.White'],

  /**
   * @deprecated
   */
  NeonYellow = COLORS['Yellow.Shade400'],

  /**
   * @deprecated
   */
  HighlightRed = COLORS['Alerts.Error.Primary'],

  /**
   * @deprecated
   */
  Green = COLORS['Green.Shade600'],

  /**
   * @deprecated
   */
  Red = COLORS['Red.Shade400'],

  /**
   * @deprecated
   */
  RedSale = COLORS['Red.Shade600'],

  /**
   * @deprecated
   */
  Blue4 = COLORS['Fill.Black'],

  /**
   * @deprecated
   */
  Green3 = COLORS['Alerts.Success.Primary'],

  /**
   * @deprecated
   */
  GreenNeon = COLORS['Alerts.Success.Primary'],

  /**
   * @deprecated
   */
  DarkGreen = COLORS['Alerts.Success.Primary'],

  /**
   * @deprecated
   */
  StartGradientSale = COLORS['Red.Shade600'],

  /**
   * @deprecated
   */
  EndGradientSale = COLORS['Orange.Shade600'],

  /**
   * @deprecated
   */
  PersonalDiscount = COLORS.Sale,

  /**
   * @deprecated
   */
  ProductImageBackground = '#F4F4F5',

  /**
   * @deprecated
   */
  PrimaryCoupon = COLORS['Blue.Shade700'],

  /**
   * @deprecated
   */
  SecondaryCoupon = COLORS['Blue.Shade600'],

  /**
   * @deprecated
   */
  BackgroundCoupon = COLORS['Blue.Shade100'],

  /**
   * @deprecated
   */
  BorderColorCoupon = COLORS['Coupon.Tertiary'],

  /**
   * @deprecated
   */
  TertiaryCoupon = COLORS['Blue.Shade500'],

  /**
   * @deprecated
   */
  LavenderBlush = COLORS['Alerts.Error.Secondary'],

  /**
   * @deprecated
   */
  LightSalmonPink = COLORS['Alerts.Error.Primary'],

  /**
   * @deprecated
   */
  NotificationsOrange = COLORS['Orange.Shade200'],

  /**
   * @deprecated
   */
  NotificationsGreen = COLORS['Alerts.Success.Tertiary'],

  /**
   * @deprecated
   */
  NotificationsOrange100 = COLORS['Orange.Shade600'],

  /**
   * @deprecated
   */
  NotificationsGreenBackgroundLight = COLORS['Green.Shade100'],

  /**
   * @deprecated
   */
  NotificationsGreen100 = COLORS['Alerts.Success.Primary'],

  /**
   * @deprecated
   */
  NotificationsRed100 = COLORS['Red.Shade600'],

  /**
   * @deprecated
   */
  NotificationsRedBackgroundLight = COLORS['Red.Shade100'],

  /**
   * @deprecated
   */
  Green300 = COLORS['Alerts.Success.Secondary'],

  /**
   * @deprecated
   */
  strikePriceGrey = COLORS['Text.Grey'],

  /**
   * @deprecated
   */
  Orange600 = COLORS['Alerts.Info.Primary'],

  /**
   * @deprecated
   */
  Orange100 = COLORS['Alerts.Info.Secondary'],
}
